import { productUnitPrice } from './productPriceOrdering';

export interface ProductPriceObservation {
  amount: number;
  currency: string;
  unit: string;
  region?: 'CONUS' | 'OCONUS' | string | null;
  configuration?: string | null;
  published_at?: string | null;
  effective_until?: string | null;
  currentness?: 'current' | 'unknown' | 'historical';
  verified: boolean;
  basis: string;
  catalog_id: string;
  document_sha256: string;
  page: number;
  source_text: string;
}

export interface ProductContractEvidence {
  catalog_id: string;
  document_sha256: string;
  page: number;
  source_text: string;
  scope: string;
  manufacturer?: string | null;
  model?: string | null;
  configuration?: string | null;
}

export function meaningfulProductText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  return !text || /^[-–—\s]+$/.test(text) || /^(?:unknown|n\/?a|not available|null|undefined)$/i.test(text)
    ? undefined : text;
}

export function productRelevanceExplanation(value: unknown): string | undefined {
  const text = meaningfulProductText(value);
  return text && !/^(?:filtering skipped|relevance (?:check |filtering )?(?:failed|unavailable)|API error|unable to (?:check|assess|verify) relevance)/i.test(text)
    ? text : undefined;
}

/** Restore literal ordering identifiers without reinterpreting award/solicitation IDs. */
export function productContractId(row: Record<string, any>): string | undefined {
  const specs = row.specifications || {};
  const solicitations = [specs.solicitation_number, specs.solicitationNumber, row.solicitation_number].filter(Boolean);
  const awards = [row.awardId, row.award_id, specs.award_id, specs.AwardID, specs.awardId].filter(Boolean);
  for (const value of [row.contractId, row.contract_id, row.contract_number,
    specs.contract_id, specs.contractId, specs.contract_number, specs.piid, row.piid, row.metadata?.contract_id,
    awards.includes(row.contractNumber) ? undefined : row.contractNumber]) {
    const text = meaningfulProductText(value);
    if (text && !solicitations.includes(text) && !/^(?:IDIQ|SABER|MACC|BPA|FSS|CARPET(?: II)?|2GIT|GSA|CONT_AWD_.*)$/i.test(text)) return text;
  }
  return undefined;
}

/** Additive metadata normalization shared by fresh, restored, and worker results. */
export function normalizeProductMetadata(row: Record<string, any>) {
  const contractId = productContractId(row);
  const comparisonPrice = productUnitPrice(row);
  return {
    contractId, contractNumber: contractId,
    contract_id: contractId ?? null,
    contract_evidence: row.contract_evidence,
    price: comparisonPrice === null ? null : row.price ?? row.unit_price,
    unit_price: row.unit_price === undefined ? undefined : comparisonPrice,
    price_status: row.price_status,
    price_region: row.price_region,
    price_observations: Array.isArray(row.price_observations) ? row.price_observations : undefined,
    relevance_confidence: row.relevance_confidence,
    relevance_check: productRelevanceExplanation(row.relevance_check),
    catalog_metadata_version: row.catalog_metadata_version,
    relevance_requirement_fingerprint: row.relevance_requirement_fingerprint,
  };
}
